import {
  useEstimatePaymentFee,
  useDepositState,
  useDepositActions,
  useDepositValidation,
  useCalculatedLimits,
} from "src/hooks";
import { PaymentType } from "src/api";
import { useUrlParamsContext, useDepositWithdrawContext } from "src/contexts";

export const useDeposit = () => {
  const { balance } = useDepositWithdrawContext();
  const { paymentRouteId, conversion: conversionFlowEnabled } = useUrlParamsContext();
  const { currency } = balance;

  const state = useDepositState();
  const {
    amount,
    amountTouched,
    setAmountTouched,
    initiateDepositLoading,
    initiateDepositError,
    ozowEstimateQuote,
    ozowEstimateQuoteLoading,
    ozowEstimateQuoteError,
  } = state;

  const { paymentFee, paymentFeeLoading, paymentFeeError } = useEstimatePaymentFee({
    paymentType: PaymentType.Deposit,
    amount: Number(amount),
    enabled: !conversionFlowEnabled,
  });

  // Live per-period used/limit/remaining (in wallet currency) for the
  // user's limit group. `availableLimit` is the tightest headroom
  // (Infinity = no cap) and gates submit via useDepositValidation.
  const { limitsStatus, availableLimit, loading: limitsLoading } = useCalculatedLimits(PaymentType.Deposit);

  const { errors } = useDepositValidation({
    amount,
    conversionFlowEnabled,
    ozowEstimateQuoteError,
    totalToReceive: ozowEstimateQuote?.total_to_receive,
    paymentFeeError,
    paymentFee,
    availableLimit,
  });

  const hasValidOzowEstimateQuote =
    !!ozowEstimateQuote &&
    (ozowEstimateQuote.source_currency_amount ?? 0) > 0 &&
    (ozowEstimateQuote.total_to_receive ?? 0) > 0;

  const actions = useDepositActions({
    amount,
    setAmount: state.setAmount,
    setIsLoading: state.setInitiateDepositLoading,
    setError: state.setInitiateDepositError,
    setSessionData: state.setSessionData,
    setOzowEstimateQuote: state.setOzowEstimateQuote,
    setOzowEstimateQuoteLoading: state.setOzowEstimateQuoteLoading,
    setOzowEstimateQuoteError: state.setOzowEstimateQuoteError,
    ozowEstimateQuoteExpiresAt: ozowEstimateQuote?.expires_at ?? null,
    conversionFlowEnabled,
    amountPrecision: currency.precision,
    paymentRouteId,
    ozowEstimateQuote,
  });

  const submitDisabled = conversionFlowEnabled
    ? ozowEstimateQuoteLoading ||
      initiateDepositLoading ||
      !!initiateDepositError ||
      !!ozowEstimateQuoteError ||
      Object.values(errors).some((error) => error.length > 0) ||
      !amount ||
      !hasValidOzowEstimateQuote
    : paymentFeeLoading ||
      initiateDepositLoading ||
      !!initiateDepositError ||
      Object.values(errors).some((error) => error.length > 0) ||
      !amount;

  return {
    amount,
    amountTouched,
    setAmountTouched,
    conversionFlowEnabled,
    ozowEstimateQuote,
    ozowEstimateQuoteLoading,
    paymentFee,
    paymentFeeLoading,
    // Platform limit group (used/limit/remaining, in wallet currency)
    limitsStatus,
    availableLimit,
    limitsLoading,
    currency,
    errors,
    submitDisabled,
    initiateDepositLoading,
    initiateDepositError,
    handleAmountChange: actions.handleAmountChange,
    handleInitiateDeposit: actions.handleInitiateDeposit,
  };
};
